import { ProjectAnalysisTab } from "@/types/projectAnalysisTab";
import { Permissions, Scenario } from "@api/apiClient";
import { useScenarios } from "@api/scenarios";
import useProjectAnalysisTabs from "@features/analysis/hooks/useProjectAnalysisTabs";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Collapse, IconButton, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
export interface IStep {
  label: string;
  content: string;
  action: () => void;
  permission: string;
}

const ProjectStepper = observer(() => {
  const { id: projectId } = useParams();
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const { data: scenarios } = useScenarios(projectId!);
  const [activeStep, setActiveStep] = useState<number>(0);
  const navigate = useNavigate();
  const upStore = useUserPermissionStore();
  const projectAnalysisTabs = useProjectAnalysisTabs();

  const steps: IStep[] = [
    {
      label: i18n.t("project-stepper.title.add-models"),
      content: i18n.t("project-stepper.content.add-models"),
      action: () => {
        navigate(`/projects/${selectionManager.selectedProject?.projectId}`);
      },
      permission: Permissions.App_Project_Model_View,
    },
    {
      label: i18n.t("project-stepper.title.add-scenarios"),
      content: i18n.t("project-stepper.content.add-scenarios"),
      action: () => {
        navigate(`/projects/${selectionManager.selectedProject?.projectId}/analysis#${projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Scenarios)}`);
      },
      permission: Permissions.App_Project_Scenario_View,
    },
    {
      label: i18n.t("project-stepper.title.assign-materials"),
      content: i18n.t("project-stepper.content.assign-materials"),
      action: () => {
        navigate(`/projects/${selectionManager.selectedProject?.projectId}/analysis#${projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Assign)}`);
      },
      permission: Permissions.App_Project_Model_Materials_View,
    },
    {
      label: i18n.t("project-stepper.title.view-results"),
      content: i18n.t("project-stepper.content.view-results"),
      action: () => {
        navigate(`/projects/${selectionManager.selectedProject?.projectId}/analysis#${projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Results)}`);
      },
      permission: Permissions.App_Project_Results_View,
    },
  ];

  const getPercentAssigned = () => {
    if (scenarios && scenarios.length > 0) {
      const totalpercentAssigned = scenarios?.reduce((acc: number, scenario: Scenario) => {
        return acc + scenario.getPercentAssigned();
      }, 0);
      const percentAssigned = totalpercentAssigned / scenarios.length;
      return percentAssigned;
    } else {
      return 0;
    }
  };

  useEffect(() => {
    if (selectionManager.selectedProject) {
      const percentAssigned = getPercentAssigned();

      if (selectionManager.selectedProject.models?.length === 0) {
        setActiveStep(0);
      } else if (scenarios?.length === 0 && selectionManager.selectedProject.models?.length !== 0) {
        setActiveStep(1);
      } else if (percentAssigned === 0) {
        setActiveStep(2);
      } else {
        setActiveStep(3);
      }
    }
  }, [selectionManager.selectedProject?.models, scenarios]);

  return (
    <>
      {appStore.isProjectStepperEnabled && (
        <Box sx={{ pt: 1, pb: 1, width: "100%" }}>
          <Collapse in={true}>
            <Alert
              severity="info"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    appStore.setIsProjectStepperEnabled(false);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ "& .MuiAlert-message": { width: "100%" } }}
            >
              <Stepper activeStep={activeStep}>
                {steps.map((step) => {
                  const isDisabled = !upStore.hasPermission(step.permission); // Check permission for each step
                  // console.log(`Step: ${step.label}, Disabled: ${isDisabled}`); // Debugging line

                  return (
                    <Step key={step.label}>
                      <StepLabel>
                        <Button variant="text" onClick={() => step.action()} disabled={isDisabled}>
                          {step.label}
                        </Button>
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              <Typography sx={{ pt: 2 }}>{steps[activeStep].content}</Typography>
            </Alert>
          </Collapse>
        </Box>
      )}
    </>
  );
});

export default ProjectStepper;
